import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Space, Input, Form, Row, Col, Tag, DatePicker, Tooltip, Modal, Typography, notification } from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import CustomSelect from '../../components/CustomSelect';
import NormalFormLabel from '../../components/NormalFormLabel';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  changeCampaignStatusRequest,
  deleteCampaignRequest,
  getListCampaignsRequest,
  getListProvincesRequest,
} from '../../services/campaignRequest';
import { LIST_STATUS_CAMPAIGN } from '../../constants/appSelect';
import { checkPermission } from '../../utils/checkPermission';
import { newObjectKeys } from '../../utils/utility';
import { useSelector } from 'react-redux';
import { getListAllPartnersRequest, getListLichVietCampaignsRequest } from '../../services/campaignStatisticRequest';
import {
  CHANGE_CAMPAIGN_STATUS,
  CREATE_UPDATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  GET_LIST_CAMPAIGNS,
} from '../../constants/apiEndPoint';
import { TableCustomV3 } from '../../components/TableCustomV3';
import { isEqual } from 'lodash';

const { RangePicker } = DatePicker;

const QuanLyChienDich = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;
  const [params, setParams] = useState({
    pageId: 1,
    limit: 20,
    status: [0, 1, 3, 4, 5],
  });

  const {
    data: dataCampaigns,
    isPending,
    refetch: refetchDataCampaigns,
  } = useQuery({
    queryKey: ['dataCampaigns', params],
    queryFn: () => getListCampaignsRequest(params),
    enabled: checkPermission(GET_LIST_CAMPAIGNS, 'Xem danh sách'),
  });

  useEffect(() => {
    const saved = sessionStorage.getItem('filterStateCampaigns');
    if (saved) {
      const filters = JSON.parse(saved);
      let tempForm = filters;
      if (filters?.start_time && filters?.end_time) {
        tempForm = { ...tempForm, timeRange: [dayjs(filters?.start_time), dayjs(filters?.end_time)] };
      }
      if (filters?.status?.length === 1) {
        tempForm = { ...tempForm, status: tempForm?.status?.[0] };
      } else {
        tempForm = { ...tempForm, status: -1 };
      }
      formSearch.setFieldsValue(tempForm);
      setParams(filters);
    }
  }, []);

  // const { data: dataProvinces } = useQuery({
  //   queryKey: ['dataProvinces'],
  //   queryFn: () => getListProvincesRequest(),
  // });

  // get data partners
  const { data: dataAllPartners } = useQuery({
    queryKey: ['dataAllPartnersCampaign'],
    queryFn: () => getListAllPartnersRequest(),
    enabled: !checkAccountIsPartner,
  });

  // convert list partners options
  const dataAllPartnersOption = useMemo(() => {
    const options =
      dataAllPartners
        ?.filter((item) => item?.status !== 2)
        ?.map((item) => {
          return { label: item?.name, value: item?.id };
        }) || [];
    return options;
  }, [dataAllPartners]);

  const { data: dataLichVietCampaigns } = useQuery({
    queryKey: ['dataLichVietCampaignsCampaign'],
    queryFn: () => getListLichVietCampaignsRequest(),
  });

  const dataLichVietCampaignsOption = useMemo(() => {
    const options =
      dataLichVietCampaigns?.map((item) => {
        return { label: item?.name, value: item?.id };
      }) || [];
    return options;
  }, [dataLichVietCampaigns]);

  const { mutate: mutateDeleteCampaign } = useMutation({
    mutationFn: deleteCampaignRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Xóa chiến dịch thành công!',
        });
        refetchDataCampaigns();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const { mutate: mutateChangeCampaignStatus } = useMutation({
    mutationFn: changeCampaignStatusRequest,
    onSuccess: async (data) => {
      if (data?.status) {
        notification.success({
          message: 'Cập nhật trạng thái chiến dịch thành công!',
        });
        refetchDataCampaigns();
      } else {
        notification.error({
          message: data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.showMessage || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleConfirmAction = (actionType, record) => {
    let modalConfig = {};
    if (actionType === 'pause' && record.status === 1) {
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn muốn tạm dừng chiến dịch này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk() {
          mutateChangeCampaignStatus({ id: record?.id.toString(), status: 3 });
        },
      };
    } else if (actionType === 'approve' && record.status === 0) {
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn muốn duyệt chiến dịch này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk() {
          mutateChangeCampaignStatus({ id: record?.id.toString(), status: 1 });
        },
      };
    } else if (actionType === 'continue' && record.status === 3) {
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn muốn tiếp tục chiến dịch này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk() {
          mutateChangeCampaignStatus({ id: record?.id.toString(), status: 1 });
        },
      };
    } else if (actionType === 'delete' && record.status !== 2) {
      modalConfig = {
        title: 'Xác nhận hành động',
        content: 'Bạn muốn xóa chiến dịch này không?',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        okType: 'danger',
        onOk() {
          mutateDeleteCampaign({ id: record?.id });
        },
      };
    }
    Modal.confirm(modalConfig);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Tên chiến dịch quảng cáo',
      dataIndex: ['name', 'id'],
      render: (_, { name, id }) => (
        <Link to={`/quan-ly-chien-dich/${id}/chinh-sua-chien-dich`} className="text-[#1677FF] hover:cursor-pointer">
          {name}
        </Link>
      ),
      minWidth: 200,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Đối tác',
      dataIndex: 'partnerName',
      key: 'partner_id',
      minWidth: 100,
      width: 150,
    },
    // {
    //   title: 'Thành phố',
    //   dataIndex: 'city_id',
    //   key: 'city_id',
    //   width: '200px',
    //   render: (city_id) =>
    //     city_id
    //       ? JSON.parse(city_id)
    //           .map(Number)
    //           ?.map((itemCityId) => (
    //             <Typography.Text>
    //               {dataProvinces?.find((itemProvince) => itemProvince?.id === itemCityId)?.name},
    //             </Typography.Text>
    //           ))
    //       : '',
    // },
    // {
    //   title: 'Giới tính',
    //   dataIndex: 'gender',
    //   key: 'gender',
    //   width: '80px',
    //   render: (gender) => {
    //     if (gender === 0) return 'Nam';
    //     if (gender === 1) return 'Nữ';
    //     return 'Tất cả';
    //   },
    // },
    // {
    //   title: 'Từ độ tuổi',
    //   dataIndex: 'min_age',
    //   key: 'min_age',
    //   width: '50px',
    // },
    // {
    //   title: 'Đến độ tuổi',
    //   dataIndex: 'max_age',
    //   key: 'max_age',
    //   width: '50px',
    // },
    // {
    //   title: 'Nền tảng',
    //   dataIndex: 'platform',
    //   key: 'platform',
    //   width: '80px',
    //   render: (platform) => {
    //     if (platform === 1) return 'Ios';
    //     if (platform === 2) return 'Android';
    //     return 'Tất cả';
    //   },
    // },    
    {
      title: 'Thời gian bắt đầu',
      dataIndex: 'start_time',
      key: 'start_time',
      render: (start_time) => <Typography.Text>{dayjs(start_time).format('HH:mm:ss DD/MM/YYYY')}</Typography.Text>,
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Thời gian kết thúc',
      dataIndex: 'end_time',
      key: 'end_time',
      render: (end_time) => <Typography.Text>{dayjs(end_time).format('HH:mm:ss DD/MM/YYYY')}</Typography.Text>,
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Số Impressions tối đa (Lượt)',
      dataIndex: 'max_impression',
      key: 'max_impression',
      width: 80,
    },
    {
      title: 'Ngân sách tối đa (VNĐ)',
      dataIndex: 'max_amount',
      key: 'max_amount',
      width: 80,
    },
    {
      title: 'Số Clicks tối đa (Lượt)',
      dataIndex: 'max_click',
      key: 'max_click',
      width: 80,
    },
    {
      title: 'Lý do từ chối',
      dataIndex: 'reason_reject',
      key: 'reason_reject',
      width: 150,
    },
    {
      title: 'Cập nhật lần cuối',
      key: 'modify_time',
      render: (_, { modify_byName, modify_time }) => (
        <Space direction="vertical">
          <Typography.Text>{modify_byName}</Typography.Text>
          <Typography.Text>{modify_time}</Typography.Text>
        </Space>
      ),
      minWidth: 90,
      width: 120,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          style={{
            backgroundColor: LIST_STATUS_CAMPAIGN.find((item) => item.value === status)?.color,
            color: 'white',
            borderRadius: '8px',
            padding: '4px 10px',
          }}
        >
          {LIST_STATUS_CAMPAIGN.find((item) => item.value === status)?.label}
        </Tag>
      ),
      width: 100,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {checkPermission(CREATE_UPDATE_CAMPAIGN, 'Chỉnh sửa') && (
            <Tooltip title="Chỉnh sửa chiến dịch quảng cáo">
              <EditOutlined
                className="text-base"
                onClick={() => navigate(`/quan-ly-chien-dich/${record?.id}/chinh-sua-chien-dich`)}
              />
            </Tooltip>
          )}

          {checkPermission(DELETE_CAMPAIGN, 'Xóa') && record?.status !== 2 && (
            <Tooltip title="Xóa chiến dịch quảng cáo">
              <DeleteOutlined className="text-base" onClick={() => handleConfirmAction('delete', record)} />
            </Tooltip>
          )}
          {checkPermission(CHANGE_CAMPAIGN_STATUS, 'Tạm dừng') && record?.status === 1 && (
            <Tooltip title="Tạm dừng chiến dịch quảng cáo">
              <PauseOutlined className="text-base" onClick={() => handleConfirmAction('pause', record)} />
            </Tooltip>
          )}

          {checkPermission(CHANGE_CAMPAIGN_STATUS, 'Tiếp tục') && record?.status === 3 && (
            <Tooltip title="Tiếp tục chiến dịch quảng cáo">
              <PlayCircleOutlined className="text-base" onClick={() => handleConfirmAction('continue', record)} />
            </Tooltip>
          )}
          {checkPermission(CHANGE_CAMPAIGN_STATUS, 'Duyệt') && record?.status === 0 && (
            <Tooltip title="Duyệt chiến dịch quảng cáo">
              <CheckCircleOutlined className="text-base" onClick={() => handleConfirmAction('approve', record)} />
            </Tooltip>
          )}
        </Space>
      ),
      width: 120,
      fixed: 'right',
      hidden:
        !checkPermission(CREATE_UPDATE_CAMPAIGN, 'Chỉnh sửa') &&
        !checkPermission(CHANGE_CAMPAIGN_STATUS, 'Tạm dừng') &&
        !checkPermission(CHANGE_CAMPAIGN_STATUS, 'Tiếp tục') &&
        !checkPermission(CHANGE_CAMPAIGN_STATUS, 'Duyệt'),
    },
  ];

  const handleSubmitForm = (values) => {
    const temp = { pageId: 1, limit: 20 };
    if (values?.timeRange) {
      Object.assign(temp, {
        start_time: dayjs(values?.timeRange?.[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end_time: dayjs(values?.timeRange?.[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
    }

    if (values?.name) {
      Object.assign(temp, { name: values?.name });
    }

    if (values?.lich_viet_campaign_ids?.length > 0) {
      Object.assign(temp, { campaign_id: values?.lich_viet_campaign_ids });
    }

    if (values?.partner_id > 0) {
      Object.assign(temp, { partner_id: values?.partner_id });
    }

    if (values?.platform > 0) {
      Object.assign(temp, { platform: values?.platform });
    }

    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    } else {
      Object.assign(temp, { status: [0, 1, 3, 4, 5] });
    }
    if (isEqual(temp, params)) {
      refetchDataCampaigns();
    } else {
      sessionStorage.setItem('filterStateCampaigns', JSON.stringify(temp));
      setParams(temp);
    }
  };

  return (
    <div className="m-6">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Danh sách chiến dịch quảng cáo</h1>
        {checkPermission(CREATE_UPDATE_CAMPAIGN, 'Tạo mới') && (
          <Button type="primary" onClick={() => navigate('/quan-ly-chien-dich/them-chien-dich')}>
            <PlusOutlined /> Thêm chiến dịch quảng cáo
          </Button>
        )}
      </div>

      {checkPermission(GET_LIST_CAMPAIGNS, 'Xem danh sách') && (
        <>
          <Form layout="vertical" name="search-campaigns" form={formSearch} onFinish={handleSubmitForm}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Thời gian" />} name="timeRange">
                  <RangePicker placeholder={['Từ ngày', 'Đến ngày']} className="h-[40px] w-full" format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Tên chiến dịch quảng cáo" />} name="name">
                  <Input allowClear placeholder="Nhập tên chiến dịch quảng cáo..." />
                </Form.Item>
              </Col>              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Chiến dịch ghi nhận đơn hàng" />} name="lich_viet_campaign_ids">
                  <CustomSelect
                    placeholder="Chiến dịch ghi nhận đơn hàng"
                    options={dataLichVietCampaignsOption}
                    mode="multiple"
                  />
                </Form.Item>
              </Col>
              {!checkAccountIsPartner && (
                <Col xs={24} sm={12} lg={6}>
                  <Form.Item label={<NormalFormLabel text="Đối tác" />} name="partner_id" initialValue={-1}>
                    <CustomSelect
                      placeholder="Đối tác"
                      options={[{ value: -1, label: 'Tất cả' }, ...dataAllPartnersOption]}
                    />
                  </Form.Item>
                </Col>
              )}
              {/* <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Nền tảng" />} name="platform" initialValue={-1}>
                  <CustomSelect
                    placeholder="Nền tảng"
                    options={[
                      { value: -1, label: 'Tất cả' },
                      { value: 1, label: 'IOS' },
                      { value: 2, label: 'Android' },
                    ]}
                  />
                </Form.Item>
              </Col> */}              <Col xs={24} sm={12} lg={6}>
                <Form.Item label={<NormalFormLabel text="Trạng thái" />} name="status" initialValue={-1}>
                  <CustomSelect
                    placeholder="Trạng thái"
                    options={[{ value: -1, label: 'Tất cả' }, ...LIST_STATUS_CAMPAIGN]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={6} className="flex items-end">
                <Form.Item>
                  <Space direction="horizontal">
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined /> Tìm kiếm
                    </Button>
                    {params &&
                      Object.values(
                        newObjectKeys(params, ['start_time', 'end_time', 'name', 'partner_id', 'platform', 'status'])
                      ).some((value) => (value && !Array.isArray(value)) || value?.length === 1) && (
                        <Button
                          htmlType="button"
                          onClick={() => {
                            formSearch.resetFields();

                            sessionStorage.setItem(
                              'filterStateCampaigns',
                              JSON.stringify({
                                pageId: 1,
                                limit: params.limit,
                                status: [0, 1, 3, 4, 5],
                              })
                            );

                            setParams({
                              pageId: 1,
                              limit: params.limit,
                              status: [0, 1, 3, 4, 5],
                            });
                          }}
                        >
                          <DeleteOutlined />
                          Xóa lọc
                        </Button>
                      )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <TableCustomV3
            isLoading={isPending}
            columns={columns}
            dataSource={dataCampaigns?.data}
            totalItem={dataCampaigns?.totalItem}
            params={params}
            setParams={setParams}
          />
        </>
      )}
    </div>
  );
};

export default QuanLyChienDich;
